import { querystringToObject } from 'gridstrap/lib/gs-utils';
import $ from 'gridstrap/lib/gs-zepto';
import * as Sentry from '@sentry/browser';
import analytics from 'gridstrap/lib/gs-analytics';

import { data } from '../data';
import { templates } from '../template';


export class GSModalService {

	//
	// Properties
	//

	private modal;

	// The open dialog's template id ('demo', 'pricing', ...), on gs:modalOpen and gs:modalClose.
	modalId;
	private document;
	private body;
	private hasReportedBlockedOpen = false;

	get isModalOpen()  { return this.body.hasClass('modal-open'); }
	set isModalOpen(value) { $('html, body').toggleClass('modal-open', !!value); }

	//
	// Public Methods
	//

	open(template, data, callback?) {
		if (this.isModalOpen) return this.reportBlockedOpen(template);
		let html = templates.render(template, data);

		$(html).appendTo('body');
		this.modal = $('.gs-Modal');
		this.modalId = template;
		this.isModalOpen = true;
		this.recordImpression();
		document.dispatchEvent(new CustomEvent('gs:modalOpen', { detail: { modalId: template } }));

		if (callback) callback();
	}

	register(modal) {
		this.modal = modal;
		this.isModalOpen = !!modal;
	}

	close() {
		if (this.modal) this.modal.gs('close');
	}

	cleanup() {
		this.recordDismiss();
		this.modal = undefined;
		this.modalId = undefined;
		this.isModalOpen = false;
	}

	// Private Methods

	// Funnel telemetry (SC-620, ADR-0004): a dialog holding a form is a
	// capture surface, and its opening is the form-intent's impression. The
	// template id names the surface ('demo', 'pricing', 'more-info', ...).
	// A dialog with no form (screenshots, sort options) is not tracked. A
	// product dialog's form carries hidden productId and context fields
	// rendered from the clicked card; the impression reads those same inputs,
	// so it records exactly what the submit would send (SC-622).
	private recordImpression() {
		const root = this.modal.get(0);
		if (!root || this.modal.find('.gs-Form').length == 0) return;
		const hidden = (name: string) => this.modal.find(`.gs-Form input[name="${name}"]`).first().val() || null;
		analytics.surface(root, 'dialog-form', String(this.modalId), {
			productId: hidden('productId'),
			context: hidden('context'),
		});
		analytics.impression(root);
	}

	// Closing the dialog, or leaving the page with it open. gs-analytics
	// drops the dismiss when the intent already has an accepted submit.
	private recordDismiss() {
		const root = this.modal && this.modal.get(0);
		if (root) analytics.dismiss(root);
	}

	// Insurance, not a funnel event: an open request while another dialog is
	// up is dropped here. Nothing on the site is meant to do that, so if it
	// ever starts, a CTA click is being eaten silently. A repeat click on the
	// CTA that opened this dialog is not reported, and a page reports once.
	private reportBlockedOpen(requested) {
		if (requested == this.modalId || this.hasReportedBlockedOpen) return;
		this.hasReportedBlockedOpen = true;
		Sentry.captureMessage('Legacy modal open dropped: another dialog was already open', {
			level: 'warning',
			fingerprint: ['legacy-modal-open-dropped'],
			extra: { open: this.modalId || null, requested, path: location.pathname },
		});
	}

	private getTemplateNameFromHash(hash: string) {
		// http://site/#:recommendations
		let pattern = /^#?:([\w-]+)/;
		let match = pattern.exec(hash);
		return match ? match[1] : undefined;
	}

	private async getDataObjectFromHash(hash: string) {
		// #:recommendations?name=value&color=blue
		let pattern = /^#:[\w-]+\?(.*)/;
		let match = pattern.exec(hash);
		let global: any = await data.get('global') || {};
		let local = match ? querystringToObject(match[1]) : {};
		return { ...global, ...local };
	}

	//
	// Initializations & Event Listeners
	//

	constructor() {
		this.document = $(document);
		this.body = $('body');

		this.document.on('click', '[data-gs-modal-template]', evt => this.handleElementClick(evt));
		this.document.on('click', 'a[href^="/"][href*="#:"]', evt => this.handleDeepLinkClick(evt));
		this.document.on('click', 'a[href^="#:"]', evt => this.handleLinkClick(evt));
		this.body.on('gs:dataAndTemplatesReady', evt => this.handlePageLoad(evt));
		window.addEventListener('pagehide', () => this.recordDismiss());
	}

	private async handlePageLoad(evt) {
		const template = this.getTemplateNameFromHash(location.hash);
		const data = await this.getDataObjectFromHash(location.hash);
		data.path = location.pathname;
		if (template) this.open(template, data);
	}

	private async handleElementClick(evt) {
		const clicked = $(evt.currentTarget);
		const button = clicked.filter('.gs-Button');
		button.gs('isProcessing', true);

		const context = clicked
			.closest('[data-gs-modal-context]')
			.data('gsModalContext') ?? '';

		const templateId = clicked.data('gsModalTemplate');
		const dataObj: any = await data.get(clicked.data('gsModalData'));
		if (!dataObj.path) dataObj.path = location.pathname;
		if (context) dataObj.context = context;

		this.open(templateId, dataObj, () => button.gs('isProcessing', false));
	}

	private async handleLinkClick(evt) {
		evt.preventDefault();
		const hash = $(evt.currentTarget).attr('href').split('#')[1];
		const template = this.getTemplateNameFromHash(hash);
		const data = await this.getDataObjectFromHash(hash);
		data.path = location.pathname;
		this.open(template, data);
	}

	private async handleDeepLinkClick(evt) {
		evt.preventDefault();
		const [path, hash] = $(evt.currentTarget).attr('href').split('#');
		const template = this.getTemplateNameFromHash(hash);
		const selector = `global, products[path=${path}](${path}#product)`;
		const dataObj: any = await data.get(selector);
		dataObj.path = location.pathname;
		this.open(template, dataObj);
	}
}
